import { Component, Input, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { ErrorLogEntry } from 'src/modules/enaio/shared/ErrorLogEntry';
import { WorkObjectAnalysesOptions } from 'src/modules/enaio/shared/WorkObjectAnalysesOptions';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { TableCellType } from 'src/modules/sm-base/models/table-cell-type.enum';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableColumn } from 'src/modules/sm-base/models/table-column.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';
import { BackendResponse } from 'src/modules/sm-base/shared/backend-response.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { DhTools } from '../../models/dh-tools.model';
import { HandlerConfigState } from '../../models/handler-config-state.model';
import { DocumentHandlerEvalConsoleComponent } from '../document-handler-eval-console/document-handler-eval-console.component';
import { DocumentHandlerProfilingComponent } from '../document-handler-profiling/document-handler-profiling.component';
import { ServerApiCallerComponent } from '../server-api-caller/server-api-caller.component';
import { DocumentHandlerStackTraceComponent } from '../document-handler-stack-trace/document-handler-stack-trace.component';
import { TableCellButton } from 'src/modules/sm-base/models/table-cell-button.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';

@Component({
  selector: 'app-document-handler-instance',
  templateUrl: './document-handler-instance.component.html',
  styleUrls: ['./document-handler-instance.component.scss']
})
export class DocumentHandlerInstanceComponent extends ComponentView {

    _Utils = Utils;

    @Input()
    guid = "";

    form: FrontendFormDefinition;
    formWorkObjectsAnalyses: FrontendFormDefinition;
    tabIndex = 0;
    state: HandlerConfigState = new HandlerConfigState();
    errors: ErrorLogEntry[] = [];
    errorTable: TableData;
    actionCountTable: TableData;
    workObjectsAnalyses: OrdinaryObject[];
    workObjectsAnalysesTable: TableData;

    actionItems: MenuItem[];
    workers: any[] = [];
    workerTable: TableData;
    workerDump = "";

    dev = false;

    @ViewChild("viewEnaioServerApiCaller") viewEnaioServerApiCaller: ServerApiCallerComponent;
    @ViewChild("viewEvalConsole") viewEvalConsole: DocumentHandlerEvalConsoleComponent;
    @ViewChild("viewProfiling") viewProfiling: DocumentHandlerProfilingComponent;
    @ViewChild("viewStackTrace") viewStackTrace: DocumentHandlerStackTraceComponent;

    async initParams(): Promise<boolean> {
        this.form = new FrontendFormDefinition([
            new FrontendFieldDefinition("active", "Status aktualisieren aktiv", FrontendFieldType.checkBox, { value: true}),
            new FrontendFieldDefinition("singleStep", "Single Step", FrontendFieldType.checkBox, { onValueChanged: this.singleStepChanged.bind(this) }),
            new FrontendFieldDefinition("singleStepAfterEveryObject", "Single Step nach jedem Objekt", FrontendFieldType.checkBox, { onValueChanged: this.singleStepAfterEveryObjectChanged.bind(this) }),
            new FrontendFieldDefinition("loggingLevelVerbose", "LogLevel: Verbose", FrontendFieldType.checkBox, { onValueChanged: async () => this.loggingLevelChanged("verbose") }),
            new FrontendFieldDefinition("loggingLevelNormal", "LogLevel: Normal", FrontendFieldType.checkBox, { onValueChanged: async () => this.loggingLevelChanged("normal") }),
            new FrontendFieldDefinition("loggingLevelSparse", "LogLevel: Sparse", FrontendFieldType.checkBox, { onValueChanged: async () => this.loggingLevelChanged("sparse") })
        ]);

        this.formWorkObjectsAnalyses = new FrontendFormDefinition([
            new FrontendFieldDefinition("grouper", "Gruppieren nach", FrontendFieldType.text, { value: "" }),
            new FrontendFieldDefinition("findDuplicates", "Duplikate finden", FrontendFieldType.checkBox),
            new FrontendFieldDefinition("filter", "Filterbedingung", FrontendFieldType.textArea, { value: "", fixedHeight: "100px" }),
            new FrontendFieldDefinition("selectFields", "Felder im Ergebnis (ein Feld pro Zeile)", FrontendFieldType.textArea, { value: "", fixedHeight: "100px" }),
            new FrontendFieldDefinition("labelOr", "---ODER---", FrontendFieldType.text, { hasControl: false}),
            new FrontendFieldDefinition("collectValues", "Werte sammeln (Scriban-Skript, das einen Array liefert)", FrontendFieldType.textArea, { value: "", fixedHeight: "100px" })
        ]);

        return Promise.resolve(true);
    }

    async updateActionItems(): Promise<void> {
        let isRemoteProfiling = await DhTools.backendCall("api/dhinstance/isRemoteProfiling", { instanceGuid: this.guid}).getBool();
        this.actionItems = Utils.arrayWithoutNull([
            {
                label: "Temporäres Verzeichnis öffnen",
                command: this.openTempDir.bind(this)
            },
            {
                label: "Heap-Dump erstellen",
                command: this.createHeapDump.bind(this)
            },
            isRemoteProfiling ? null : {
                label: "Remote-Profiling starten",
                command: this.startRemoteProfiling.bind(this)
            },
            !isRemoteProfiling ? null : {
                label: "Remote-Profiling stoppen und Ausgabedatei erzeugen",
                command: this.stopRemoteProfiling.bind(this)
            }
        ]);
    }

    async onTimer(): Promise<boolean> {
        switch (this.tabIndex) {
            case 0:
                if (this.form.getValue("active")) {
                    try {
                        this.state = await DhTools.backendCall("api/dhinstance/state", { instanceGuid: this.guid }).get(HandlerConfigState);
                        this.form.setValue("loggingLevelVerbose", this.state.verbose);
                        this.form.setValue("loggingLevelNormal", !this.state.verbose && !this.state.sparse);
                        this.form.setValue("loggingLevelSparse", !this.state.verbose && this.state.sparse);
                        this.form.setValue("singleStep", this.state.singleStep);
                        this.form.setValue("singleStepAfterEveryObject", this.state.singleStepAfterEveryObject);
                        if (this.state.errorCount > this.errors.length) {
                            this.errors = [...this.errors, ...await DhTools.backendCall("api/dhinstance/getErrors", { instanceGuid: this.guid, fromIndex: this.errors.length }).list(ErrorLogEntry)];
                            this.updateErrorTable();
                        }
                        this.actionCountTable = TableData.update(this.actionCountTable, [
                            new TableColumn("count", "Anzahl", TableCellType.int, { autoWidth: 7 }),
                            new TableColumn("type", "Typ", TableCellType.text, { autoWidth: 40 })
                        ], Utils.arraySortBy(this.state.actionCount, k => k.key.toString()).map(kvp => new TableRow("", kvp.key, {
                            type: kvp.key.toString(),
                            count: kvp.value
                        })));
                    }
                    catch (ex) {
                        let error = BackendResponse.getFromException(ex);
                        if (error.isErrorId(1000) || error.isErrorId(1001)) {
                            if (this.state == null) {
                                this.state = new HandlerConfigState();
                            }
                            this.state.finished = true;
                            this.form.setValue("active", false);
                            return false;
                        }
                    }
                }
                break;
            case 1:
                this.workers = await DhTools.backendCall("api/dhinstance/getWorkers", { instanceGuid: this.guid }).list(Object);
                this.workerTable = TableData.update(this.workerTable, [
                    new TableColumn("name", "Name", TableCellType.text, { flex: 1 })
                ], this.workers.map(w => new TableRow("", w, {
                    name: w.DisplayName
                })));
                break;
            case 4:
                await this.viewProfiling.update();
                break;
            case 5:
                await this.viewStackTrace.update();
                break;
        }
        return true;
    }


    async activeTabChanged(): Promise<void> {
        if (this.tabIndex == 6) {
            await this.viewEnaioServerApiCaller.initEmbedded();
        }
    }

    async loggingLevelChanged(type: string): Promise<void> {
        this.form.setValue("loggingLevelVerbose", type == "verbose");
        this.form.setValue("loggingLevelNormal", type == "normal");
        this.form.setValue("loggingLevelSparse", type == "sparse");
        await DhTools.backendCall("api/dhinstance/setGlobal", { instanceGuid: this.guid, sparse: type == "sparse", verbose: type == "verbose"}).getText();
    }

    async singleStepChanged(): Promise<void> {
        await DhTools.backendCall("api/dhinstance/setGlobal", { instanceGuid: this.guid, singleStep: this.form.getValue("singleStep")}).getText();
    }

    async singleStepAfterEveryObjectChanged(): Promise<void> {
        await DhTools.backendCall("api/dhinstance/setGlobal", { instanceGuid: this.guid, singleStepAfterEveryObject: this.form.getValue("singleStepAfterEveryObject")}).getText();
    }

    async break(): Promise<void> {
        await DhTools.backendCall("api/dhinstance/setGlobal", { instanceGuid: this.guid, breakpointRequested: true}).getText();
    }

    async continue(): Promise<void> {
        await DhTools.backendCall("api/dhinstance/setGlobal", { instanceGuid: this.guid, continueRequested: true}).getText();
    }

    async openTempDir(): Promise<void> {
        let tempPath = await DhTools.backendCall("api/dhinstance/getTempPath", { instanceGuid: this.guid}).getText();
        await DhTools.backendCall("api/system/openFile", { fileName: tempPath }).getText();
    }

    async createHeapDump(): Promise<void> {
        await DhTools.backendCall("api/dhinstance/dumpHeap", { instanceGuid: this.guid}).getText();
        this.app.showToast("info", "Info", "Heap Dump wurde erstellt");
    }

    async startRemoteProfiling(): Promise<void> {
        await DhTools.backendCall("api/dhinstance/startRemoteProfiling", { instanceGuid: this.guid}).getText();
        this.app.showToast("info", "Info", "Remote-Profiling gestartet");
    }

    async stopRemoteProfiling(): Promise<void> {
        await DhTools.backendCall("api/dhinstance/stopRemoteProfiling", { instanceGuid: this.guid}).getText();
        this.app.showToast("info", "Info", "Remote-Profiling gestoppt und Ausgabedatei erzeugt");
    }

    updateErrorTable(): void {
        this.errorTable = new TableData([
            new TableColumn("_tools", "", TableCellType.button, { autoWidth: 2 }),
            new TableColumn("date", "Datum", TableCellType.dateTime, { autoWidth: 1 }),
            new TableColumn("exceptionType", "Exception", TableCellType.text, { autoWidth: 25 }),
            new TableColumn("errorMessage", "Fehlermeldung", TableCellType.text, { flex: 1 }),
            new TableColumn("workerName", "Worker", TableCellType.text, { autoWidth: 20 }),
            new TableColumn("objectName", "Objekt", TableCellType.text, { autoWidth: 20 }),
            new TableColumn("objectIndex", "Objektindex", TableCellType.int, { autoWidth: 6 })
        ], this.errors.map(error => new TableRow("", error, {
            _tools: [new TableCellButton("details", "fas fa-eye", "Details"), new TableCellButton("evaluate", "fas fa-code", "Auswerten")],
            date: error.date,
            exceptionType: error.exceptionType,
            errorMessage: new TableCell(Utils.replaceAll(error.errorMessage, "\n", "<br>"), { styleClass: "monospace"}),
            workerName: error.workerName,
            objectName: error.objectName,
            objectIndex: error.objectIndex
        })), [new TableSortColumn("date")]);
    }

    async analyseWorkObjects(): Promise<void> {
        let collectValues = (this.formWorkObjectsAnalyses.getValue("collectValues") as string)?.trim();
        let options = Utils.fromPlain(WorkObjectAnalysesOptions, {
            grouper: this.formWorkObjectsAnalyses.getValue("grouper"),
            findDuplicates: this.formWorkObjectsAnalyses.getValue("findDuplicates"),
            filter: this.formWorkObjectsAnalyses.getValue("filter"),
            selectFields: Utils.splitLines(this.formWorkObjectsAnalyses.getValue("selectFields") as string).filter(f => f != ""),
            collectValues
        });
        this.workObjectsAnalyses = await DhTools.backendCall("api/dhinstance/analyseWorkObjects", {instanceGuid: this.guid, options: Utils.toJson(options)}).listObjects();

        if (!Utils.isNoe(collectValues)) {
            this.workObjectsAnalysesTable = new TableData([
                new TableColumn("value", "Wert", TableCellType.text, { flex: 1 }),
                new TableColumn("count", "Anzahl", TableCellType.int, { autoWidth: 7 })
            ], this.workObjectsAnalyses.map(wo => new TableRow("", wo, wo)), [new TableSortColumn("count", false)]);
        }
        else {
            this.workObjectsAnalysesTable = new TableData([
                new TableColumn("index", "Index", TableCellType.int, { autoWidth: 5 }),
                new TableColumn("id", "ID", TableCellType.text, { flex: 1 }),
                !Utils.isNoe(options.grouper) ? new TableColumn("itemCount", "Anzahl Objekte", TableCellType.int, { autoWidth: 8 }) : null,
                ...options.selectFields.map(f => new TableColumn(f, f))
            ], this.workObjectsAnalyses.map(wo => new TableRow("", wo, wo)), [!Utils.isNoe(options.grouper) ? new TableSortColumn("itemCount", false) : new TableSortColumn("index")]);
        }
    }

    async workObjectsToCsv(unique: boolean): Promise<void> {
        let fileName = await DhTools.saveDialog("workObjects.csv");

        await DhTools.backendCall("api/dhinstance/workObjectsToCsv", { instanceGuid: this.guid, fileName, unique}).getText();
    }

    async errorCellClicked(event: TableCellEvent): Promise<void> {
        let e = event.row.raw as ErrorLogEntry;
        if (event.isButton("details", "_tools")) {
            this.app.messageDialog.textArea("Fehler", e.raw, null, true);
        }
        else if (event.isButton("evaluate", "_tools")) {
            this.viewEvalConsole.objectIndex = Utils.toString(e.objectIndex);
            await this.viewEvalConsole.evaluateAll();
            this.tabIndex = 3;
        }
    }

    workerCellClicked(event): void {
        let e = event.raw as OrdinaryObject;
        this.workerDump = Utils.toJson(e, true);
    }

}
